"use client";
import {
  bscDepositContractAbi,
  bscLoanDepositContractAbi,
  ethDepositContractAbi,
  ethLoanDepositContractAbi,
  paxgDepositApproveAbi,
  usdtBEP20ApproveAbi,
  usdtERC20ApproveAbi,
} from "@/contract/abi";
import {
  loanDepositBSCContractAddress,
  loanDepositETHContractAddress,
  paxgAddress,
  tradeDepositBSCContractAddress,
  tradeDepositETHContractAddress,
  usdtBEP20Addresss,
  usdtERC20Addresss,
} from "@/contract/address";
import { useWalletConnection } from "@/hooks";
import { ContextCreator } from "@/libs";
import { UserContext } from "@/contexts/UserDataContext";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Address } from "viem";
import { DepositTokenNameEnum, DepositTokenTypeEnum } from "@/libs/api/enums";

export type DepositToken = {
  id: number;
  name: DepositTokenNameEnum.DepositTokenName;
  type: DepositTokenTypeEnum.DepositTokenType;
  icon: string;
  disabled?: boolean;
  network?: "ETH" | "BSC" | "";
  contractAbi?: typeof ethDepositContractAbi;
  loanContractAbi?: typeof ethLoanDepositContractAbi;
  decimal?: number;
  approveAbi?:
    typeof paxgDepositApproveAbi | typeof usdtERC20ApproveAbi | string;
  contractAddress?: Address;
  loanContractAddress?: Address;
  tradeContractAddress?: Address;
  approveAddress?: Address;
  functionName?: string;
  loanFunctionName?: string;
  accountBalance?: number;
  price?: number;
};
interface InitialValues {
  contracts: DepositToken[];
  keys?: {
    contractAddressDepositBSC?: Address;
    contractAddressDepositETH?: Address;
    contractAddressWithdrawBSC?: Address;
    contractAddressWithdrawETH?: Address;
  };
  contractNetworkName?: (type: string) => string;
}
export type { InitialValues as ContractAddressesInitialValues };

const initialValue: InitialValues = { contracts: [] };
export const [ContractAddressesProvider, useContractAddresses] =
  ContextCreator(initialValue);

export function ContractAddressesContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isConnected } = useWalletConnection();
  const { data } = useContext(UserContext);
  const [keys, setKeysState] = useState<InitialValues["keys"]>({});

  const setKeys = (newKeys: InitialValues) => {
    setKeysState((prev) => ({ ...prev, ...newKeys.keys }));
  };

  useEffect(() => {
    if (data?.team) {
      // eslint-disable-next-line
      setKeysState({
        contractAddressDepositBSC: data?.team
          ?.contractAddressDepositBSC as Address,
        contractAddressDepositETH: data?.team
          ?.contractAddressDepositETH as Address,
        contractAddressWithdrawBSC: data?.team
          ?.contractAddressWithdrawBSC as Address,
        contractAddressWithdrawETH: data?.team
          ?.contractAddressWithdrawETH as Address,
      });
    }
  }, [data, isConnected]);

  
  const contracts: DepositToken[] = useMemo(() => {
    return [
      {
        id: 1,
        name: "PAXG",
        type: "PAXG",
        icon: "/images/paxg-icon.svg",
        network: "ETH",
        approveAbi: paxgDepositApproveAbi,
        contractAbi: ethDepositContractAbi,
        loanContractAbi: ethLoanDepositContractAbi,
        approveAddress: paxgAddress,
        contractAddress: keys?.contractAddressDepositETH,
        loanContractAddress: loanDepositETHContractAddress,
        tradeContractAddress: tradeDepositETHContractAddress,
        functionName: "depositTokens",
        decimal: 18,
        disabled: false,
      },
      {
        id: 2,
        name: "USDT",
        type: "TetherERC20",
        icon: "/images/tether-icon.png",
        network: "ETH",
        approveAbi: usdtERC20ApproveAbi,
        contractAbi: ethDepositContractAbi,
        loanContractAbi: ethLoanDepositContractAbi,
        approveAddress: usdtERC20Addresss,
        contractAddress: keys?.contractAddressDepositETH,
        loanContractAddress: loanDepositETHContractAddress,
        tradeContractAddress: tradeDepositETHContractAddress,
        functionName: "depositTokens",
        decimal: 6,
        disabled: false,
      },
      {
        id: 3,
        name: "USDT",
        type: "TetherBEP20",
        icon: "/images/usdt-icon.svg",
        network: "BSC",
        approveAbi: usdtBEP20ApproveAbi,
        contractAbi: bscDepositContractAbi,
        loanContractAbi: bscLoanDepositContractAbi,
        approveAddress: usdtBEP20Addresss,
        contractAddress: keys?.contractAddressDepositBSC,
        loanContractAddress: loanDepositBSCContractAddress,
        tradeContractAddress: tradeDepositBSCContractAddress,
        functionName: "depositTokens",
        loanFunctionName: "depositTokens",
        decimal: 18,
        disabled: false,
      },
      {
        id: 4,
        name: "BNB",
        type: "BNB",
        icon: "/images/bnb-logo.svg",
        network: "BSC",
        approveAbi: "",
        contractAbi: bscDepositContractAbi,
        loanContractAbi: bscLoanDepositContractAbi,
        contractAddress: keys?.contractAddressDepositBSC,
        loanContractAddress: loanDepositBSCContractAddress,
        tradeContractAddress: tradeDepositBSCContractAddress,
        functionName: "depositBNB",
        decimal: 18,
        disabled: false,
      },
      {
        id: 5,
        name: "ETH",
        type: "ETH",
        icon: "/images/et-symbol.svg",
        network: "ETH",
        approveAbi: "",
        contractAbi: ethDepositContractAbi,
        contractAddress: keys?.contractAddressDepositETH,
        tradeContractAddress: tradeDepositETHContractAddress,
        functionName: "depositETH",
        decimal: 18,
        disabled: true,
      },
    ];
  }, [tradeDepositETHContractAddress, keys]);

  const contractNetworkName = (type: string) =>
    contracts.find((item) => item.type === type)?.network;

  return (
    <ContractAddressesProvider
      value={[{ contracts, keys, contractNetworkName }, setKeys]}
    >
      {children}
    </ContractAddressesProvider>
  );
}
